/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * selfCheckInRules
 * ----------------
 * The pure half of participant self check-in: when the option is offered, what
 * it says on each seat, and whether the details somebody typed are enough.
 * No requests, no React — `now` is always an argument, so every hour of the
 * day is testable.
 *
 * THE RULES ARE THE DESK'S, NOT A SECOND SET. The window, the late mark, the
 * check-out cut-off and the double-tap guard all come from checkInRules, which
 * reads them from the same four environment values the organizer desk reads.
 * Two doors into one room must not disagree about when the room is open, and
 * the only way to guarantee that is to ask the same module.
 *
 * WHAT A SEAT'S NEXT STEP IS COMES FROM THE PASS, never from a switch the
 * participant could set — exactly as runScan decides it at the desk:
 *
 *   ISSUED       → check in (details first)
 *   CHECKED_IN   → check out, or, within RESCAN_MIN, "check out anyway"
 *   CHECKED_OUT  → nothing; a pass checks in and out once
 *   anything else (CANCELLED, blank) → nothing, with the reason
 */

import {
  eventWindow, idOf, clockIn, nameOf, infoOf, RESCAN_MIN, CLOSED_STATUSES,
  OPENS_BEFORE_MIN,
} from '../../organizer/checkin/checkInRules';
import envConfig from '../../../config/envConfig';
import { parseStoredUtc } from '../../../utils/zonedDateTime';

/**
 * Whether the clock is allowed to close the participant's door at all.
 *
 * The default for every call, taken from the environment, so a deployment
 * decides it once. Callers may override it per call, which is what the tests
 * do — and what a per-event setting would use if one is ever wanted.
 */
const NO_EXPIRY = envConfig.selfCheckInNoExpiry;

/** Statuses a pass can be in that this flow knows what to do with. */
const ISSUED = 'ISSUED';
const CHECKED_IN = 'CHECKED_IN';
const CHECKED_OUT = 'CHECKED_OUT';

/** What a seat's button does next. 'none' means no button at all. */
export const SEAT_ACTION = {
  CHECK_IN: 'check_in',
  CHECK_OUT: 'check_out',
  RESCAN: 'rescan',
  NONE: 'none',
};

/** Only this event's passes, out of everything the visitor holds. */
export function passesForEvent(passes, eventId) {
  if (eventId === undefined || eventId === null) return [];
  return (passes || []).filter((pass) => String(idOf(pass.event_id)) === String(eventId));
}

/**
 * The four details a seat carries, read off whatever the pass already knows.
 *
 * A pass issued to the person who booked carries their account details; a seat
 * they booked for somebody else carries little or nothing. Both arrive here as
 * the same shape so the form has one contract, and a missing piece is '' rather
 * than undefined — a controlled input must never be handed undefined.
 */
export function detailsFrom(userInfo) {
  const info = infoOf(userInfo) || {};
  return {
    first: String(info.first_name || '').trim(),
    last: String(info.last_name || '').trim(),
    mobile: String(info.mobile_number || '').trim(),
    email: String(info.email || '').trim(),
  };
}

/**
 * The same address test registration applies, so a mail that is good enough to
 * register with is good enough at the gate, and one that is not is refused in
 * the same words at both ends.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * How long each field may be.
 *
 * These are the registration form's own limits, not new ones: a name that was
 * good enough to register with must be good enough at the gate, and a value
 * the column cannot hold should be refused in the browser rather than by a
 * database error nobody can act on. 254 is the longest an email address may
 * be; 20 covers a country code and the longest national number in use.
 */
export const FIELD_LIMITS = { first: 60, last: 60, email: 254, mobile: 20 };

/** Digits only, ignoring the spaces, brackets and + people type. */
function mobileDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/**
 * A mobile is optional, but a mobile that IS given has to be a number — a
 * certificate chase or a marshal's call goes nowhere otherwise. Between 6 and
 * 15 digits covers every national format plus a country code (ITU E.164 caps
 * at 15), and rejects the "12345" and "call me" a hurried person types.
 */
function mobileLooksRight(value) {
  const raw = String(value ?? '').trim();
  // Not given at all is fine; a mobile is optional.
  if (!raw) return true;
  // TESTED ON THE RAW VALUE, not on its digits. "call me" reduces to no digits
  // at all, and treating that as "none given" would let somebody past with a
  // mobile field full of words — which is not the same as leaving it blank.
  const digits = mobileDigits(raw);
  return digits.length >= 6 && digits.length <= 15 && raw.length <= FIELD_LIMITS.mobile;
}

/** Fields over their ceiling, in the order the form shows them. */
function tooLong(values) {
  return Object.keys(FIELD_LIMITS)
    .filter((key) => String(values?.[key] ?? '').trim().length > FIELD_LIMITS[key]);
}

/**
 * Are these details enough to check somebody in?
 *
 * A FIRST NAME AND AN EMAIL, both required, because this is what a certificate
 * is made from: the name is printed on it and the address is where it goes. A
 * surname and a mobile are offered and kept, but neither is insisted on — a
 * person at a gate should not be stopped over a surname they do not use.
 *
 * Returned as separate facts rather than one boolean so the form can say which
 * piece is missing, and say "that address does not look right" rather than
 * refusing the whole form without explaining itself.
 */
export function detailsCheck(values) {
  const email = String(values?.email || '').trim();
  const named = Boolean(String(values?.first || '').trim());
  const hasEmail = Boolean(email);
  const over = tooLong(values);
  const emailValid = hasEmail && EMAIL.test(email) && !over.includes('email');
  const mobileValid = mobileLooksRight(values?.mobile);
  return {
    named: named && !over.includes('first'),
    hasEmail,
    emailValid,
    mobileValid,
    over,
    // Kept under its old name so callers that only ask "may I send this yet"
    // read the same way they always did.
    reachable: emailValid,
    ok: named && emailValid && mobileValid && over.length === 0,
  };
}

/** What is still missing, in words, or '' when nothing is. */
export function detailsMissing(values) {
  const check = detailsCheck(values);
  if (check.ok) return '';

  const LABEL = { first: 'first name', last: 'last name', email: 'email', mobile: 'mobile' };
  const wants = [];
  // A field over its ceiling is named for what it is, not as "missing" — the
  // person typed something, and being told it is absent would be nonsense.
  check.over.forEach((key) => wants.push(`a shorter ${LABEL[key]} — that ${LABEL[key]} is too long`));

  if (!check.named && !check.over.includes('first')) wants.push('a first name');
  if (!check.hasEmail) wants.push('an email for the certificate');
  else if (!check.emailValid && !check.over.includes('email')) wants.push('an email that looks right');
  if (!check.mobileValid && !check.over.includes('mobile')) wants.push('a mobile that is a real number');
  return wants.join(', ');
}

/**
 * What the pass's user_info should become once somebody has confirmed their
 * details at the gate.
 *
 * A MERGE, NOT A REPLACEMENT, and that is the whole reason this function
 * exists. The write path sets a json column to exactly what it is given, so
 * sending only the four confirmed fields would delete everything else the
 * registration captured — gender, age, the country code — without anybody
 * noticing until a certificate or an export came out wrong. So the pass's own
 * values are carried through untouched, and only the four are overwritten.
 *
 * A field the person CLEARED is removed rather than stored as an empty string:
 * "they deleted it" and "it was never given" should not read differently to
 * the desk, the export, or whatever prints the certificate.
 *
 * The mobile is reduced to digits, because that is how registration stores it —
 * the country code lives in its own key, and a number typed with spaces or a
 * +91 in front must not end up shaped differently from one typed at signup.
 */
export function mergedUserInfo(userInfo, values) {
  const next = { ...(infoOf(userInfo) || {}) };

  const put = (key, raw) => {
    const value = String(raw ?? '').trim();
    if (value) next[key] = value;
    else delete next[key];
  };

  put('first_name', values?.first);
  put('last_name', values?.last);
  put('email', values?.email);

  const digits = String(values?.mobile ?? '').replace(/\D/g, '');
  if (digits) next.mobile_number = digits;
  else {
    delete next.mobile_number;
    // The code belongs to a number that is no longer there.
    delete next.country_code;
  }

  return next;
}

/** Whole minutes since an instant, or null when there is no instant to use. */
function minutesSince(value, now) {
  const at = value instanceof Date ? value : parseStoredUtc(value);
  if (!at) return null;
  return Math.max(0, Math.round((now.getTime() - at.getTime()) / 60000));
}

/**
 * One seat's state: what it is called, where it stands, and what a tap does.
 *
 * WHERE THE TIME COMES FROM. A pass does not carry its check-in time — that
 * row lives in event_check_in, which a participant cannot read. What the pass
 * does carry is `updated_at`, and the status and that timestamp are written in
 * the SAME all-or-nothing save, so on a pass whose status is CHECKED_IN it is
 * the moment of the check-in. Used as that, and never presented as anything
 * more precise. `checked_in_at` is read first for the one caller that has it
 * (the desk, which did read the row) so this stays usable from both sides.
 *
 * The time only ever improves the wording — the status is what decides the
 * action. A page that cannot work out a time still offers the right button, it
 * simply says "Checked in" rather than "Checked in at 6:12 am", and the
 * double-tap guard is skipped: a guard that cannot measure ten minutes must
 * not invent an answer and refuse a legitimate check-out.
 */
export function seatStateOf(pass, timing, now) {
  const status = String(pass?.status || '').toUpperCase();
  const details = detailsFrom(pass?.user_info);
  const seat = {
    passId: pass?.id,
    passcode: String(pass?.passcode || ''),
    name: nameOf(pass?.user_info) || '',
    details,
    // Everything the pass already knows, kept whole so a confirmation can be
    // merged into it rather than written over it. See mergedUserInfo.
    userInfo: infoOf(pass?.user_info) || {},
    status,
    checkedInAt: null,
    action: SEAT_ACTION.NONE,
    note: '',
  };

  if (status === CHECKED_OUT) {
    seat.note = 'Checked in and out already. A pass checks in and out once.';
    return seat;
  }

  if (status === CHECKED_IN) {
    const changed = pass?.checked_in_at || pass?.updated_at || null;
    const since = minutesSince(changed, now);
    seat.checkedInAt = changed ? clockIn(changed, timing?.timeZone) : null;
    if (timing?.checkOutClosed) {
      seat.note = 'Check-out has closed for this event.';
      return seat;
    }
    if (since != null && since < RESCAN_MIN) {
      seat.action = SEAT_ACTION.RESCAN;
      seat.note = since < 1
        ? 'Just checked in moments ago — that looks like a double tap.'
        : `Just checked in, ${since} min ago — that looks like a double tap.`;
      return seat;
    }
    seat.action = SEAT_ACTION.CHECK_OUT;
    return seat;
  }

  if (status === ISSUED) {
    seat.action = SEAT_ACTION.CHECK_IN;
    return seat;
  }

  // CANCELLED, or a status this flow does not know. Fails closed, with a
  // reason: a pass we cannot explain must not carry a button.
  seat.note = status
    ? `This pass is ${status.toLowerCase().replace(/_/g, ' ')}, so it cannot be used.`
    : 'This pass has no status, so it cannot be used.';
  return seat;
}

/**
 * Everything the event page needs to draw the check-in section, or `visible:
 * false` when there is nothing to draw.
 *
 * SHOWN WHEN THE DESK WOULD TAKE THIS PASS — the event is open for check-in on
 * its own clock — or when a seat has already been through a gate, so somebody
 * who checked in can still see that they did and still check out. Hidden
 * entirely otherwise, which covers: not registered, event not open yet,
 * yesterday's event, draft and cancelled events.
 */
export function selfCheckInView({
  passes, event, eventId, now = new Date(), noExpiry = NO_EXPIRY,
}) {
  const mine = passesForEvent(passes, eventId ?? event?.id);
  if (!mine.length) return { visible: false, seats: [] };

  const measured = { ...eventWindow(event, now), timeZone: event?.timezone };
  const timing = holdOpen(measured, event, noExpiry);
  const seats = mine.map((pass) => seatStateOf(pass, timing, now));
  const been = seats.some((seat) => seat.status === CHECKED_IN || seat.status === CHECKED_OUT);

  if (!timing.isOpen && !been) return { visible: false, seats: [] };

  const actionable = seats.filter((seat) => seat.action !== SEAT_ACTION.NONE);
  return {
    visible: true,
    open: timing.isOpen,
    phase: timing.phase,
    timeZone: event?.timezone,
    // What the strip says about the clock. One line, decided here so the
    // component has no wording of its own to drift.
    windowLabel: windowLabel(timing, event),
    seats,
    pending: seats.filter((seat) => seat.action === SEAT_ACTION.CHECK_IN).length,
    inCount: seats.filter((seat) => seat.status === CHECKED_IN).length,
    actionable: actionable.length,
  };
}

/** The one line that says where the clock is, in the event's own zone. */
export function windowLabel(timing, event) {
  if (!timing) return '';
  if (timing.phase === 'not_open') {
    return timing.opensAt
      ? `Opens ${clockIn(timing.opensAt, event?.timezone)}` : 'Not open yet';
  }
  if (!timing.isOpen) return 'Closed — the event’s day has ended';
  if (timing.phase === 'ended') {
    return `Event ended ${clockIn(timing.end, event?.timezone)} — still open today`;
  }
  if (timing.phase === 'late') {
    return `Event started ${clockIn(timing.start, event?.timezone)}`;
  }
  return 'Open now';
}

/**
 * The window as the participant's door should see it.
 *
 * Without `noExpiry` this is the measured window, untouched — the shipped
 * behaviour, and the reason turning the switch off restores it exactly.
 *
 * With it, the clock stops being a reason to CLOSE — never a reason to open
 * early. Once the window has opened (start − OPENS_BEFORE_MIN, the same moment
 * the organizer's desk opens) `isOpen` stays true for good and the check-out
 * cut-off is lifted, so a registered participant can still check in or out
 * after the event's day has ended, however long ago. Three things it
 * deliberately does NOT do:
 *
 *   - It does not open the door before OPENS_BEFORE_MIN. 'not_open' is left
 *     exactly as measured, so a participant cannot check in a day early.
 *   - It does not open a DRAFT or CANCELLED event. That is not an expiry, it is
 *     the event not being real, and offering check-in for a cancelled event
 *     would be a lie to the participant.
 *   - It does not touch `eventWindow` itself, so the ORGANIZER's desk — which
 *     asks that function directly — keeps its own hours.
 *
 * A phase of 'ended' becomes 'on_time', because the line under the heading
 * must not say "still open today" on a door that no longer closes at midnight.
 * 'late' is left alone: "Event started 06:00" is still true and still useful.
 */
export function holdOpen(timing, event, noExpiry) {
  if (!noExpiry) return timing;
  if (CLOSED_STATUSES.has(String(event?.status || '').toUpperCase())) return timing;
  if (timing.phase === 'not_open') return timing;
  return {
    ...timing,
    isOpen: true,
    phase: timing.phase === 'ended' ? 'on_time' : timing.phase,
    checkOutClosed: false,
  };
}

export { RESCAN_MIN };

/** "1 hour", "2 hours", "90 minutes" — OPENS_BEFORE_MIN as a person says it. */
export function opensBeforeLabel(minutes = OPENS_BEFORE_MIN) {
  const value = Number(minutes) || 0;
  if (value > 0 && value % 60 === 0) {
    const hours = value / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return value === 1 ? '1 minute' : `${value} minutes`;
}

/**
 * The line shown right after somebody registers: when they can check
 * themselves in, and where.
 *
 * THE SAME WINDOW THE DESK OPENS, read from eventWindow, so this never promises
 * a door the pass card will not offer. With an event it names the clock time
 * in the event's own zone; without one (the checkout return page knows only
 * the order) it states the rule. '' for a draft or cancelled event, and for
 * one whose day has already ended — there is nothing left to promise.
 *
 * WHERE IS ALWAYS MY REGISTRATIONS. Self check-in lives on each pass card
 * there, and nowhere else, so every surface points at it — the event page
 * included, which no longer has a check-in of its own.
 */
export const CHECK_IN_WHERE = 'You can check in from My Registrations.';

export function checkInOpensHint(event, now = new Date()) {
  const before = opensBeforeLabel();
  const generic = `Self check-in opens ${before} before the event starts. ${CHECK_IN_WHERE}`;
  if (!event) return generic;
  if (CLOSED_STATUSES.has(String(event.status || '').toUpperCase())) return '';

  const timing = eventWindow(event, now);
  if (!timing.start) return generic;
  if (timing.isOpen) {
    return 'Self check-in is open now — check in from My Registrations.';
  }
  if (timing.phase !== 'not_open') return '';
  const at = clockIn(timing.opensAt, event.timezone);
  return at
    ? `Self check-in opens at ${at}, ${before} before the event starts. ${CHECK_IN_WHERE}`
    : generic;
}

/** "16 Oct, 5:30 am" in the event's own zone, or '' when there is no instant. */
function dayAndClock(instant, timeZone) {
  if (!instant) return '';
  let day = '';
  try {
    day = new Intl.DateTimeFormat('en-GB', { timeZone: timeZone || undefined, day: 'numeric', month: 'short' })
      .format(instant);
  } catch (e) {
    day = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(instant);
  }
  const clock = clockIn(instant, timeZone);
  return [day, clock].filter(Boolean).join(', ');
}

/**
 * The self check-in button on one pass card in My Registrations.
 *
 * One pass, one button: the same decision the check-in dialog makes for that
 * seat (selfCheckInView over just this pass), reduced to what the card draws.
 *
 *   kind     'check_in' | 'check_out' — what pressing it opens; 'none' when it
 *            is only a label; 'hidden' when the card shows no button at all
 *   label    the button's text
 *   enabled  whether it can be pressed
 *
 * `event` is the event's own public row (it carries the status and end time
 * the window needs; a pass's related `event` does not). Without it there is
 * nothing to decide from, so the button stays hidden rather than guessing.
 */
export function passCardState(pass, event, now = new Date(), { noExpiry = NO_EXPIRY } = {}) {
  const hidden = { kind: 'hidden', label: '', enabled: false };
  if (!pass || !event) return hidden;
  const status = String(pass.status || '').toUpperCase();
  if (status && status !== ISSUED && status !== CHECKED_IN && status !== CHECKED_OUT) return hidden;

  const shut = (label) => ({ kind: 'none', label, enabled: false });
  if (CLOSED_STATUSES.has(String(event.status || '').toUpperCase())) {
    return shut('Self check-in unavailable');
  }

  const view = selfCheckInView({
    passes: [{ ...pass, event_id: event.id }], event, eventId: event.id, now, noExpiry,
  });
  const seat = view.seats && view.seats[0];

  if (view.visible && view.open && seat) {
    if (seat.action === SEAT_ACTION.CHECK_IN) return { kind: 'check_in', label: 'Self check-in', enabled: true };
    if (seat.action === SEAT_ACTION.CHECK_OUT || seat.action === SEAT_ACTION.RESCAN) {
      return { kind: 'check_out', label: 'Self check-out', enabled: true };
    }
  }

  if (status === CHECKED_OUT) return shut('Checked out');
  if (status === CHECKED_IN) return shut('Checked in');

  const timing = eventWindow(event, now);
  if (timing.phase === 'not_open' && timing.opensAt) {
    return shut(`Self check-in opens ${dayAndClock(timing.opensAt, event.timezone)}`);
  }
  return shut('Self check-in closed');
}

/**
 * Which of the events ON SCREEN are open for check-in to somebody holding one
 * of these passes — what the events list needs to say "check-in is open".
 *
 * THE EVENTS COME FROM THE LIST, not from the passes. A pass's related `event`
 * carries a title and a start time but no end time, and the window needs both,
 * so the authoritative row is the one the list already fetched. The pass is
 * asked one question only: do I hold one for this event.
 *
 * It therefore only ever speaks about the page in front of the visitor. An
 * event on page three of the list is not mentioned — correct for a notice,
 * which is all this is: it points at the event page, where the check-in
 * actually happens.
 */
export function openCheckInsFor({ passes, events, now = new Date() }) {
  if (!Array.isArray(passes) || !passes.length) return [];
  return (events || []).reduce((open, event) => {
    const view = selfCheckInView({ passes, event, eventId: event?.id, now });
    // Only events with something to do: a seat already checked out is state,
    // not an errand, and a notice that cannot be acted on is noise.
    if (view.visible && view.open && view.actionable) {
      open.push({
        eventId: event.id,
        title: event.title || 'this event',
        windowLabel: view.windowLabel,
        seats: view.seats.length,
        pending: view.pending,
        inCount: view.inCount,
      });
    }
    return open;
  }, []);
}
